// src/pages/profile-settings/patient/EditAddress.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import supabase from "@/services/supabaseClient";

import TopBar from "@/sot/primitives/TopBar.jsx";
import InputCountrySADC from "@/components/fields/InputCountrySADC.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import { resolveCurrentAddress } from "@/lib/addressLocation.js";

import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";

function normalizeText(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.toLowerCase() === "[object object]" ? "" : trimmed;
  }
  if (typeof value === "number") return String(value);
  if (value && typeof value === "object") {
    return normalizeText(
      value.label || value.name || value.value || value.country || value.code || ""
    );
  }
  return "";
}

function isFrenchLanguageCode(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

export default function EditAddress() {
  const nav = useNavigate();
  const { refreshProfile } = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);

  const [line1, setLine1] = React.useState("");     // Number, Street
  const [line2, setLine2] = React.useState("");     // City, Suburb
  const [postal, setPostal] = React.useState("");   // Post code
  const [country, setCountry] = React.useState(""); // Country (SADC list)
  const [locBusy, setLocBusy] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const locationRequestRef = React.useRef({ id: 0, controller: null });
  const addressRevisionRef = React.useRef(0);

  const [isQuickFillDialogOpen, setIsQuickFillDialogOpen] =
    React.useState(false);

  const canSave = Boolean(line1 || line2 || postal || country);

  // ────────────────────────────────────────────────────────────────
  // Hydrate from local intro preview (single source for EditIntro)
  // ────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    let cancelled = false;

    async function hydrateAddress() {
      const preview = loadPatientIntroPreview() || {};
      const previewLine1 = normalizeText(preview.addressLine || preview.addressStreet || "");
      const previewLine2 = normalizeText(preview.addressLine2 || preview.city || "");
      const previewPostal = normalizeText(preview.postalCode || preview.addressPostalCode || "");
      const previewCountry = normalizeText(preview.country || preview.addressCountry || "");

      if (previewLine1 && !cancelled) setLine1(previewLine1);
      if (previewLine2 && !cancelled) setLine2(previewLine2);
      if (previewPostal && !cancelled) setPostal(previewPostal);
      if (previewCountry && !cancelled) setCountry(previewCountry);

      try {
        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();

        if (authErr || !user?.id) return;

        const { data, error } = await supabase
          .from("patient_profiles")
          .select("street_address, city, postal_code, country")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error || !data || cancelled) return;

        const dbLine1 = normalizeText(data.street_address || "");
        const dbLine2 = normalizeText(data.city || "");
        const dbPostal = normalizeText(data.postal_code || "");
        const dbCountry = normalizeText(data.country || "");

        // DB must win when present so mobile/desktop stay aligned.
        setLine1(dbLine1 || previewLine1);
        setLine2(dbLine2 || previewLine2);
        setPostal(dbPostal || previewPostal);
        setCountry(dbCountry || previewCountry);
      } catch {
        // silent fallback to manual input
      }
    }

    hydrateAddress();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => () => locationRequestRef.current.controller?.abort(), []);

  const changeAddressField = (setter) => (value) => {
    addressRevisionRef.current += 1;
    setter(value);
  };

  // ────────────────────────────────────────────────────────────────
  // Location quick-fill
  // ────────────────────────────────────────────────────────────────
  async function useMyLocation() {
    setSaveError("");
    locationRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const requestId = locationRequestRef.current.id + 1;
    const startingRevision = addressRevisionRef.current;
    locationRequestRef.current = { id: requestId, controller };
    setLocBusy(true);
    try {
      const { address } = await resolveCurrentAddress({ signal: controller.signal });
      if (locationRequestRef.current.id !== requestId || addressRevisionRef.current !== startingRevision) return;
      setLine1((current) => address.streetAddress || current);
      setLine2((current) => address.city || current);
      setPostal((current) => address.postalCode || current);
      setCountry((current) => address.country || current);
    } catch {
      if (locationRequestRef.current.id !== requestId || controller.signal.aborted) return;
      setSaveError(
        t(
          "settings.edit.address.lookupFailed",
          isFrench
            ? "Nous n’avons pas pu trouver votre adresse. Saisissez-la manuellement ou réessayez."
            : "We couldn’t find your address. Enter it manually or try again."
        )
      );
    } finally {
      if (locationRequestRef.current.id === requestId) setLocBusy(false);
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Save → write into patientIntroPreview and go back
  // ────────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!canSave || locBusy) return;
    setSaveError("");

    const cleanLine1 = normalizeText(line1);
    const cleanLine2 = normalizeText(line2);
    const cleanPostal = normalizeText(postal);
    const cleanCountry = normalizeText(country);
    const current = loadPatientIntroPreview() || {};
    const next = {
      ...current,
      addressLine: cleanLine1,
      addressStreet: cleanLine1,
      addressLine2: cleanLine2,
      city: cleanLine2,
      postalCode: cleanPostal,
      addressPostalCode: cleanPostal,
      country: cleanCountry,
      addressCountry: cleanCountry,
    };

    savePatientIntroPreview(next);

    try {
      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (authErr || !user?.id) {
        setSaveError(
          t(
            "settings.edit.address.signInRequired",
            isFrench ? "Veuillez vous connecter pour enregistrer votre adresse." : "Please sign in to save your address."
          )
        );
        return;
      }

      const { error: upErr } = await supabase
        .from("patient_profiles")
        .upsert(
          {
            user_id: user.id,
            street_address: cleanLine1 || null,
            city: cleanLine2 || null,
            postal_code: cleanPostal || null,
            country: cleanCountry || null,
          },
          { onConflict: "user_id" }
        );

      if (upErr) throw upErr;

      try {
        await refreshProfile?.();
      } catch {
        // non-fatal
      }

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        nav("/settings/patient/p", { replace: true });
      }, 2200);
    } catch (e) {
      setSaveError(
        e?.message ||
          t(
            "settings.edit.address.saveFailed",
            isFrench ? "Impossible d’enregistrer votre adresse. Veuillez réessayer." : "We couldn't save your address. Please try again."
          )
      );
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────
  return (
    <SettingsScreenShell>
      <TopBar
        title={t("settings.edit.address.title", isFrench ? "Modifier l’adresse" : "Edit Address")}
        subtitle={t(
          "settings.edit.address.subtitle",
          isFrench ? "Assurez-vous de saisir correctement votre adresse." : "Make sure you enter your address correctly."
        )}
        onBack={() => nav("/settings/patient/p")}
        trailing={<SaveChip disabled={!canSave} onClick={handleSave} />}
      />

      <div className="mt-5">
        <FolaOnboardingCard role="patient" />
      </div>

      <div className="mt-5 md:mt-6 space-y-5">
        {/* Use my location (opens confirmation dialog) */}
        <button
          type="button"
          onClick={() => setIsQuickFillDialogOpen(true)}
          disabled={locBusy}
          className="inline-flex items-center gap-2 h-9 px-0 rounded-lg text-body-medium text-primary disabled:opacity-60"
          style={{ background: "transparent" }}
        >
          <img
            src="/icons/doctor/near_me.svg"
            alt=""
            className="h-[18px] w-[18px]"
            aria-hidden="true"
          />
          <span>
            {locBusy
              ? t("settings.edit.address.locating", isFrench ? "Localisation..." : "Locating...")
              : t(
                  "settings.edit.address.useCurrentLocation",
                  isFrench ? "Utiliser ma position actuelle" : "Use my current location"
                )}
          </span>
        </button>

        <div className="space-y-5">
          <TextField56
            label={t("settings.edit.address.line1Label", isFrench ? "Adresse" : "Address")}
            value={line1}
            onChange={changeAddressField(setLine1)}
            placeholder={t(
              "settings.edit.address.line1Placeholder",
              isFrench ? "Numéro, rue" : "Number, Street"
            )}
            tone="surface"
          />
          <TextField56
            label={t("settings.edit.address.line2Label", isFrench ? "Adresse (ligne 2)" : "Address (line 2)")}
            value={line2}
            onChange={changeAddressField(setLine2)}
            placeholder={t(
              "settings.edit.address.line2Placeholder",
              isFrench ? "Ville, quartier" : "City, Suburb"
            )}
            tone="surface"
          />
          <TextField56
            label={t("settings.edit.address.postalLabel", isFrench ? "Code postal" : "Postal code")}
            value={postal}
            onChange={changeAddressField(setPostal)}
            placeholder={t(
              "settings.edit.address.postalPlaceholder",
              isFrench ? "Code postal" : "Post code"
            )}
            tone="surface"
          />
          <InputCountrySADC
            label={t("settings.edit.address.countryLabel", isFrench ? "Pays" : "Country")}
            value={country}
            onChange={(next) => changeAddressField(setCountry)(normalizeText(next))}
            tone="surface"
            placeholderFlagIso="za"
          />
        </div>
      </div>

      {/* Quick-fill dialog (same AfriMed style as doctor) */}
      {isQuickFillDialogOpen && (
        <div className="fixed inset-0 z-[1000] grid place-items-center bg-black/40">
          <div className="w-[92vw] max-w-[480px] rounded-2xl bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06),0_8px_24px_rgba(16,24,40,0.06)]">
            <div className="px-5 pt-5 pb-3">
              <h2 className="text-title-large text-on-surface">
                {t(
                  "settings.edit.address.quickFillTitle",
                  isFrench ? "Remplir l’adresse automatiquement ?" : "Fill address automatically?"
                )}
              </h2>
              <p className="text-body-medium text-on-surface-var mt-1">
                {t(
                  "settings.edit.address.quickFillBodyPatient",
                  isFrench
                    ? "Nous pouvons utiliser votre position actuelle pour remplir rapidement votre adresse afin que vous n’ayez pas à la saisir manuellement."
                    : "We can use your current location to quickly fill in your home address so you don't have to type it manually."
                )}
              </p>
            </div>

            <div className="h-px mx-5 bg-[rgba(24,50,119,0.10)]" />

            <div className="px-5 py-4 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setIsQuickFillDialogOpen(false)}
                className="px-5 py-2.5 rounded-xl text-label-large border"
                style={{
                  borderColor: "rgba(24,50,119,0.18)",
                  background: "transparent",
                  color: "var(--on-surface)",
                }}
              >
                {t("common.keepEditing", isFrench ? "Continuer la modification" : "Keep editing")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsQuickFillDialogOpen(false);
                  useMyLocation();
                }}
                className="px-5 py-2.5 rounded-xl text-label-large"
                style={{
                  background: "var(--state-primary-12)",
                  color: "var(--on-primary)",
                }}
              >
                {t("settings.edit.address.quickFillAction", isFrench ? "Oui, remplir automatiquement" : "Yes, quick fill")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global “Changes saved” Snackbar */}
      <Snackbar open={saved} onClose={() => setSaved(false)}>
        {t("common.changesSaved", isFrench ? "Modifications enregistrées" : "Changes saved")}
      </Snackbar>

      {!!saveError && (
        <Snackbar
          open={Boolean(saveError)}
          onClose={() => setSaveError("")}
          duration={2800}
          variant="error"
        >
          {saveError}
        </Snackbar>
      )}
    </SettingsScreenShell>
  );
}
